import { useQuery, useMutation, useQueryClient, type UseQueryOptions, type UseMutationOptions } from '@tanstack/react-query'
import {
  aiStudioService,
  type CustomAgentSummary,
  type CustomAgentDetail,
  type CreateAgentFromScratchRequest,
  type CreateAgentFromPresetRequest,
  type UpdateCustomAgentRequest,
  type AgentValidationResult,
  type AgentCostEstimate,
  type AgentTestRunResult,
  type AgentTestCase,
  type CreateTestCaseRequest,
  type UpdateTestCaseRequest,
  type TestCaseExecutionResult,
  type SkillLibraryItem,
  type CreateSkillRequest,
  type UpdateSkillRequest,
  type SkillCategory,
  type PromptTemplate,
  type AgentStats,
} from '@/lib/api/services/aiStudio'

// Query Keys
export const aiStudioKeys = {
  all: ['ai-studio'] as const,
  stats: () => [...aiStudioKeys.all, 'stats'] as const,
  agents: () => [...aiStudioKeys.all, 'agents'] as const,
  agentsList: (includeInactive?: boolean, includePresets?: boolean) => [...aiStudioKeys.agents(), 'list', { includeInactive, includePresets }] as const,
  agentDetail: (id: string) => [...aiStudioKeys.agents(), 'detail', id] as const,
  agentValidation: (id: string) => [...aiStudioKeys.agents(), 'validation', id] as const,
  agentCost: (id: string) => [...aiStudioKeys.agents(), 'cost', id] as const,
  agentTestCases: (agentId: string) => [...aiStudioKeys.agents(), 'test-cases', agentId] as const,
  skills: () => [...aiStudioKeys.all, 'skills'] as const,
  skillsList: (category?: SkillCategory, search?: string) => [...aiStudioKeys.skills(), 'list', { category, search }] as const,
  skillDetail: (id: string) => [...aiStudioKeys.skills(), 'detail', id] as const,
  promptTemplates: () => [...aiStudioKeys.all, 'prompt-templates'] as const,
  promptTemplatesList: (category?: string) => [...aiStudioKeys.promptTemplates(), 'list', { category }] as const,
  promptTemplateDetail: (id: string) => [...aiStudioKeys.promptTemplates(), 'detail', id] as const,
}

// Agent Statistics Query
export function useAgentStats(
  options?: Omit<UseQueryOptions<AgentStats, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: aiStudioKeys.stats(),
    queryFn: () => aiStudioService.getAgentStats(),
    staleTime: 30000, // 30 seconds
    ...options,
  })
}

// Agent Queries
export function useCustomAgents(
  includeInactive: boolean = false,
  includePresets: boolean = false,
  options?: Omit<UseQueryOptions<CustomAgentSummary[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: aiStudioKeys.agentsList(includeInactive, includePresets),
    queryFn: () => aiStudioService.getCustomAgents(includeInactive, includePresets),
    ...options,
  })
}

export function useCustomAgent(
  id: string,
  options?: Omit<UseQueryOptions<CustomAgentDetail, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: aiStudioKeys.agentDetail(id),
    queryFn: () => aiStudioService.getAgent(id),
    enabled: !!id,
    ...options,
  })
}

export function useAgentValidation(
  id: string,
  options?: Omit<UseQueryOptions<AgentValidationResult, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: aiStudioKeys.agentValidation(id),
    queryFn: () => aiStudioService.validateAgent(id),
    enabled: !!id,
    ...options,
  })
}

export function useAgentCostEstimate(
  id: string,
  options?: Omit<UseQueryOptions<AgentCostEstimate, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: aiStudioKeys.agentCost(id),
    queryFn: () => aiStudioService.estimateCost(id),
    enabled: !!id,
    ...options,
  })
}

export function useAgentTestCases(
  agentId: string,
  options?: Omit<UseQueryOptions<AgentTestCase[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: aiStudioKeys.agentTestCases(agentId),
    queryFn: () => aiStudioService.getTestCases(agentId),
    enabled: !!agentId,
    ...options,
  })
}

// Agent Mutations
export function useCreateAgentFromScratch(
  options?: UseMutationOptions<CustomAgentDetail, Error, CreateAgentFromScratchRequest>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAgentFromScratchRequest) => aiStudioService.createFromScratch(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agents() })
    },
    ...options,
  })
}

export function useCreateAgentFromPreset(
  options?: UseMutationOptions<CustomAgentDetail, Error, CreateAgentFromPresetRequest>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAgentFromPresetRequest) => aiStudioService.createFromPreset(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agents() })
    },
    ...options,
  })
}

export function useUpdateCustomAgent(
  options?: UseMutationOptions<CustomAgentDetail, Error, { id: string; data: UpdateCustomAgentRequest }>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }) => aiStudioService.updateAgent(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agents() })
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agentDetail(id) })
    },
    ...options,
  })
}

export function useDeleteCustomAgent(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => aiStudioService.deleteAgent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agents() })
    },
    ...options,
  })
}

export function useValidateAgent(
  options?: UseMutationOptions<AgentValidationResult, Error, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => aiStudioService.validateAgent(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agentValidation(id) })
    },
    ...options,
  })
}

export function useRunAgentTests(
  options?: UseMutationOptions<AgentTestRunResult, Error, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => aiStudioService.runTests(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agentDetail(id) })
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agentTestCases(id) })
    },
    ...options,
  })
}

export function useDeployAgent(
  options?: UseMutationOptions<CustomAgentDetail, Error, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => aiStudioService.deployAgent(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agents() })
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agentDetail(id) })
    },
    ...options,
  })
}

export function useDeprecateAgent(
  options?: UseMutationOptions<CustomAgentDetail, Error, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => aiStudioService.deprecateAgent(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agents() })
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agentDetail(id) })
    },
    ...options,
  })
}

// Test Case Mutations
export function useCreateTestCase(
  options?: UseMutationOptions<AgentTestCase, Error, CreateTestCaseRequest>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTestCaseRequest) => aiStudioService.addTestCase(data),
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agentTestCases(agentId) })
    },
    ...options,
  })
}

export function useUpdateTestCase(
  options?: UseMutationOptions<AgentTestCase, Error, { testCaseId: string; data: UpdateTestCaseRequest; agentId: string }>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ testCaseId, data }) => aiStudioService.updateTestCase(testCaseId, data),
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agentTestCases(agentId) })
    },
    ...options,
  })
}

export function useDeleteTestCase(
  options?: UseMutationOptions<void, Error, { testCaseId: string; agentId: string }>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ testCaseId }) => aiStudioService.deleteTestCase(testCaseId),
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agentTestCases(agentId) })
    },
    ...options,
  })
}

export function useRunSingleTest(
  options?: UseMutationOptions<TestCaseExecutionResult, Error, { testCaseId: string; agentId: string }>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ testCaseId }) => aiStudioService.runSingleTest(testCaseId),
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agentTestCases(agentId) })
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agentDetail(agentId) })
    },
    ...options,
  })
}

// Skill Library Queries
export function useSkillLibrary(
  category?: SkillCategory,
  search?: string,
  options?: Omit<UseQueryOptions<SkillLibraryItem[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: aiStudioKeys.skillsList(category, search),
    queryFn: () => aiStudioService.getSkillLibrary(category, search),
    ...options,
  })
}

export function useSkill(
  id: string,
  options?: Omit<UseQueryOptions<SkillLibraryItem, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: aiStudioKeys.skillDetail(id),
    queryFn: () => aiStudioService.getSkill(id),
    enabled: !!id,
    ...options,
  })
}

// Skill Mutations
export function useCreateSkill(
  options?: UseMutationOptions<SkillLibraryItem, Error, CreateSkillRequest>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateSkillRequest) => aiStudioService.createSkill(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.skills() })
    },
    ...options,
  })
}

export function useUpdateSkill(
  options?: UseMutationOptions<SkillLibraryItem, Error, { id: string; data: UpdateSkillRequest }>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }) => aiStudioService.updateSkill(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.skills() })
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.skillDetail(id) })
    },
    ...options,
  })
}

export function useDeleteSkill(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => aiStudioService.deleteSkill(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.skills() })
    },
    ...options,
  })
}

export function useAddSkillsToAgent(
  options?: UseMutationOptions<CustomAgentDetail, Error, { agentId: string; skillIds: string[] }>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ agentId, skillIds }) => aiStudioService.addSkillsToAgent(agentId, skillIds),
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agentDetail(agentId) })
    },
    ...options,
  })
}

export function useRemoveSkillFromAgent(
  options?: UseMutationOptions<CustomAgentDetail, Error, { agentId: string; skillId: string }>
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ agentId, skillId }) => aiStudioService.removeSkillFromAgent(agentId, skillId),
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: aiStudioKeys.agentDetail(agentId) })
    },
    ...options,
  })
}

// Prompt Template Queries
export function usePromptTemplates(
  category?: string,
  options?: Omit<UseQueryOptions<PromptTemplate[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: aiStudioKeys.promptTemplatesList(category),
    queryFn: () => aiStudioService.getPromptTemplates(category),
    ...options,
  })
}

export function usePromptTemplate(
  id: string,
  options?: Omit<UseQueryOptions<PromptTemplate, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: aiStudioKeys.promptTemplateDetail(id),
    queryFn: () => aiStudioService.getPromptTemplate(id),
    enabled: !!id,
    ...options,
  })
}

export function useApplyPromptTemplate(
  options?: UseMutationOptions<{ text: string }, Error, { templateId: string; variables: Record<string, string> }>
) {
  return useMutation({
    mutationFn: ({ templateId, variables }) => aiStudioService.applyPromptTemplate(templateId, variables),
    ...options,
  })
}
